import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Nfc as NfcIcon,
  SwapHoriz as SwapHorizIcon,
} from "@mui/icons-material";
import { useNfcKeyboardScan } from "../../hooks/useNfcKeyboardScan";

export interface NfcCardScanInputProps {
  value: string;
  onChange: (value: string) => void;
  onScanComplete: (cardNumber: string) => void;
  hideScannedValue?: boolean;
  resolvedDisplayNumber?: string | null;
  hideInputAfterScan?: boolean;
  loading?: boolean;
  allowRescan?: boolean;
  onClear?: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  label?: string;
  hint?: string;
}

const NfcCardScanInput: React.FC<NfcCardScanInputProps> = ({
  value,
  onChange,
  onScanComplete,
  hideScannedValue = false,
  resolvedDisplayNumber = null,
  hideInputAfterScan = false,
  loading = false,
  allowRescan = false,
  onClear,
  disabled = false,
  autoFocus = true,
  label = "Card",
  hint = "Tap NFC card on reader — card will be added automatically",
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const scanBufferRef = useRef("");
  const [listening, setListening] = useState(true);
  const [scanInputKey, setScanInputKey] = useState(0);
  /** When true, show scan UI even before parent clears resolvedDisplayNumber */
  const [awaitingScan, setAwaitingScan] = useState(true);
  const lastScanRef = useRef("");
  const processingRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const burstCountRef = useRef(0);
  const lastKeyTimeRef = useRef(0);

  const parentLinked = Boolean(resolvedDisplayNumber?.trim());
  const isLinked = parentLinked && !awaitingScan;
  const showScanInput = !isLinked && (awaitingScan || !hideInputAfterScan || listening || loading);

  const clearScanField = useCallback(() => {
    scanBufferRef.current = "";
    if (inputRef.current) inputRef.current.value = "";
    onChange("");
  }, [onChange]);

  const focusScanInput = useCallback(() => {
    window.setTimeout(() => inputRef.current?.focus(), 80);
  }, []);

  const readFieldValue = useCallback(() => {
    if (hideScannedValue) {
      return (inputRef.current?.value ?? scanBufferRef.current).trim();
    }
    return value.trim();
  }, [hideScannedValue, value]);

  const handleScan = useCallback(
    (raw: string) => {
      if (processingRef.current || disabled || loading) return;
      const cardNumber = raw.trim();
      if (!cardNumber) return;

      processingRef.current = true;
      lastScanRef.current = cardNumber;
      scanBufferRef.current = cardNumber;
      setListening(false);
      setAwaitingScan(true);

      if (hideScannedValue) {
        clearScanField();
      } else {
        onChange(cardNumber);
      }

      onScanComplete(cardNumber);
    },
    [clearScanField, disabled, hideScannedValue, loading, onChange, onScanComplete]
  );

  const handleChangeCard = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    processingRef.current = false;
    lastScanRef.current = "";
    setListening(true);
    setAwaitingScan(true);
    clearScanField();
    setScanInputKey((k) => k + 1);
    onClear?.();
    focusScanInput();
  };

  useNfcKeyboardScan({
    enabled: allowRescan && isLinked && !loading && !disabled,
    onScan: (cardNumber) => {
      if (cardNumber === lastScanRef.current) {
        lastScanRef.current = "";
      }
      onClear?.();
      setAwaitingScan(true);
      handleScan(cardNumber);
    },
    minLength: 3,
  });

  useEffect(() => {
    if (parentLinked && !loading) {
      setAwaitingScan(false);
    }
  }, [parentLinked, loading, resolvedDisplayNumber]);

  useEffect(() => {
    if (!parentLinked && !loading) {
      setAwaitingScan(true);
      processingRef.current = false;
      lastScanRef.current = "";
      setListening(true);
    }
  }, [parentLinked, loading]);

  useEffect(() => {
    if (!loading) {
      processingRef.current = false;
    }
  }, [loading]);

  useEffect(() => {
    if (autoFocus && !disabled && showScanInput && !loading) {
      focusScanInput();
    }
  }, [autoFocus, disabled, showScanInput, loading, scanInputKey, focusScanInput]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const current = readFieldValue();
      if (current) {
        handleScan(current);
      }
    }
  };

  const handleChange = (next: string) => {
    const now = Date.now();
    if (now - lastKeyTimeRef.current < 80) {
      burstCountRef.current += 1;
    } else {
      burstCountRef.current = 1;
    }
    lastKeyTimeRef.current = now;

    scanBufferRef.current = next;
    if (!hideScannedValue) {
      onChange(next);
    }

    if (!next.trim()) {
      lastScanRef.current = "";
      setListening(true);
      burstCountRef.current = 0;
      return;
    }

    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      const trimmed = next.trim();
      const looksLikeReader =
        burstCountRef.current >= 4 ||
        (trimmed.length >= 8 && burstCountRef.current >= 2);
      if (
        looksLikeReader &&
        trimmed.length >= 6 &&
        !processingRef.current
      ) {
        handleScan(trimmed);
      }
      burstCountRef.current = 0;
    }, 180);
  };

  return (
    <Box data-nfc-scan-capture="true" sx={{ position: "relative" }}>
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          mb: 2,
          textAlign: "center",
          borderRadius: 1,
          borderStyle: "dashed",
          borderColor: !isLinked && listening ? "primary.main" : "divider",
          bgcolor: !isLinked && listening ? "action.selected" : "action.hover",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 72,
            height: 72,
            borderRadius: "50%",
            bgcolor: "background.paper",
            mb: 1,
            animation:
              !isLinked && listening && !disabled && !loading
                ? "nfcPulse 1.5s ease-in-out infinite"
                : "none",
            "@keyframes nfcPulse": {
              "0%, 100%": { boxShadow: "0 0 0 0 rgba(25, 118, 210, 0.35)" },
              "50%": { boxShadow: "0 0 0 12px rgba(25, 118, 210, 0)" },
            },
          }}
        >
          {disabled || loading ? (
            <CircularProgress size={36} />
          ) : isLinked ? (
            <CheckCircleIcon sx={{ fontSize: 40, color: "success.main" }} />
          ) : (
            <NfcIcon sx={{ fontSize: 40, color: "primary.main" }} />
          )}
        </Box>

        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {isLinked
            ? "Card linked"
            : loading
              ? "Verifying card…"
              : listening
                ? "Ready — tap NFC card"
                : "Card detected"}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {isLinked ? resolvedDisplayNumber : hint}
        </Typography>

        <Chip
          size="small"
          icon={isLinked ? <CheckCircleIcon /> : <NfcIcon />}
          label={
            isLinked
              ? "Card ready"
              : loading
                ? "Looking up card…"
                : listening
                  ? "Listening for reader…"
                  : "Processing…"
          }
          color={isLinked ? "success" : listening ? "primary" : "default"}
          variant={!isLinked && listening ? "filled" : "outlined"}
        />

        {isLinked && allowRescan && onClear && (
          <Box sx={{ mt: 1.5 }}>
            <Button
              type="button"
              size="small"
              variant="outlined"
              startIcon={<SwapHorizIcon />}
              onClick={handleChangeCard}
              disabled={loading || disabled}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Change card
            </Button>
          </Box>
        )}
      </Paper>

      {isLinked ? (
        <TextField
          fullWidth
          label={label}
          value={resolvedDisplayNumber ?? ""}
          disabled
          InputProps={{ readOnly: true }}
        />
      ) : showScanInput ? (
        hideScannedValue ? (
          <TextField
            key={`nfc-scan-${scanInputKey}`}
            inputRef={inputRef}
            fullWidth
            label={label}
            defaultValue=""
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Tap card on reader "
            disabled={disabled || loading}
            autoFocus={autoFocus}
            inputProps={{ autoComplete: "off", "data-nfc-scan-input": true }}
          />
        ) : (
          <TextField
            key={`nfc-scan-${scanInputKey}`}
            inputRef={inputRef}
            fullWidth
            label={label}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Or type display code, then Enter"
            disabled={disabled || loading}
            autoFocus={autoFocus}
            inputProps={{ autoComplete: "off", "data-nfc-scan-input": true }}
          />
        )
      ) : null}
    </Box>
  );
};

export default NfcCardScanInput;
